/**
 * HH Finance - Bank Accounts Table Component
 */

import React from 'react';
import styled from 'styled-components';
import { Card } from '@shared/components/card';
import { formatCurrency } from '../utils';
import type { BankAccount } from '../types';

export interface BankAccountsTableProps {
  accounts: BankAccount[];
  onAccountClick?: (account: BankAccount) => void;
}

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary || '#1a1a1a'};
  margin: 0;
`;

const Icon = styled.span`
  font-size: 20px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 8px;
  border-bottom: 2px solid ${({ theme }) => theme.border || '#e5e5e5'};
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary || '#666'};
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:last-child {
    text-align: right;
  }
`;

const Tr = styled.tr<{ clickable?: boolean }>`
  cursor: ${({ clickable }) => clickable ? 'pointer' : 'default'};

  &:hover {
    background-color: ${({ theme, clickable }) =>
      clickable ? theme.backgroundHover || '#f9fafb' : 'transparent'};
  }
`;

const Td = styled.td`
  padding: 12px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.border || '#f0f0f0'};
  font-size: 14px;
  color: ${({ theme }) => theme.textPrimary || '#1a1a1a'};

  &:last-child {
    text-align: right;
  }
`;

const AccountName = styled.span`
  font-weight: 500;
`;

const AccountType = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary || '#666'};
  margin-left: 8px;
`;

const Balance = styled.span<{ positive: boolean }>`
  font-weight: 600;
  color: ${({ theme, positive }) =>
    positive ? theme.success || '#22c55e' : theme.error || '#ef4444'};
`;

const TotalRow = styled.tr`
  background-color: ${({ theme }) => theme.backgroundSecondary || '#f9fafb'};
`;

const TotalLabel = styled.td`
  padding: 12px 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary || '#1a1a1a'};
`;

const TotalValue = styled.td<{ positive: boolean }>`
  padding: 12px 8px;
  text-align: right;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme, positive }) =>
    positive ? theme.success || '#22c55e' : theme.error || '#ef4444'};
`;

export const BankAccountsTable: React.FC<BankAccountsTableProps> = ({
  accounts,
  onAccountClick,
}) => {
  const total = accounts.reduce((sum, acc) => sum + acc.balance, 0);

  return (
    <Card>
      <Header>
        <Icon>🏦</Icon>
        <Title>Bank Accounts</Title>
      </Header>

      <Table>
        <thead>
          <tr>
            <Th>Account</Th>
            <Th>Balance</Th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((account) => (
            <Tr
              key={account.accountId}
              clickable={!!onAccountClick}
              onClick={() => onAccountClick?.(account)}
            >
              <Td>
                <AccountName>{account.accountName}</AccountName>
                {account.accountType !== 'Bank' && (
                  <AccountType>({account.accountType})</AccountType>
                )}
              </Td>
              <Td>
                <Balance positive={account.balance >= 0}>
                  {formatCurrency(account.balance)}
                </Balance>
              </Td>
            </Tr>
          ))}
          <TotalRow>
            <TotalLabel>Total Cash</TotalLabel>
            <TotalValue positive={total >= 0}>
              {formatCurrency(total)}
            </TotalValue>
          </TotalRow>
        </tbody>
      </Table>
    </Card>
  );
};

export default BankAccountsTable;
